import { useState, useEffect, useRef } from 'react';
import {
  collection, query, where, getDocs,
  addDoc, updateDoc, deleteDoc, doc, getDoc, setDoc, serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { useAuth } from '../hooks/useAuth';
import { logAdminActivity } from '../services/adminActivityLog';
import {
  PlusIcon, PencilIcon, TrashIcon, EyeIcon, EyeSlashIcon,
  PhotoIcon, LinkIcon, DocumentTextIcon, XMarkIcon, ArrowUpTrayIcon,
  ChevronUpIcon, ChevronDownIcon
} from '@heroicons/react/24/outline';

const TARGET_TABS = [
  { key: 'driver', label: 'Driver App' },
  { key: 'customer', label: 'Customer App' },
];

const TYPE_OPTIONS = [
  { value: 'text', label: 'Text', icon: DocumentTextIcon },
  { value: 'image', label: 'Image', icon: PhotoIcon },
];

const FONT_SIZES = [
  { value: 11, label: 'Small' },
  { value: 13, label: 'Medium' },
  { value: 16, label: 'Large' },
  { value: 19, label: 'X-Large' },
];

const emptyForm = {
  type: 'text', text: '', url: '', imageUrl: '',
  textColor: '#FFFFFF', bgColor: '#EA580C',
  fontSize: 13, bold: true,
};

export default function Banners() {
  const { user: currentAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('driver');
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [closeable, setCloseable] = useState({ driver: true, customer: true });
  const [savingCloseable, setSavingCloseable] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { fetchBanners(); }, [activeTab]);

  useEffect(() => {
    getDoc(doc(db, 'app_settings', 'banners_config')).then(d => {
      if (d.exists()) setCloseable(prev => ({ ...prev, ...d.data() }));
    }).catch(() => {});
  }, []);

  const toggleCloseable = async (target) => {
    const next = { ...closeable, [target]: !closeable[target] };
    setCloseable(next);
    setSavingCloseable(true);
    try {
      await setDoc(doc(db, 'app_settings', 'banners_config'), next, { merge: true });
    } catch (err) {
      console.error(err);
    } finally {
      setSavingCloseable(false);
    }
  };

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'banners'),
        where('target', '==', activeTab)
      );
      const snap = await getDocs(q);
      setBanners(
        snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      );
    } catch (err) {
      console.error('Error fetching banners:', err);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingBanner(null);
    setForm(emptyForm);
    setSelectedImage(null);
    setImagePreview(null);
    setShowModal(true);
  };

  const openEdit = (banner) => {
    setEditingBanner(banner);
    setForm({
      type: banner.type,
      text: banner.text || '',
      url: banner.url || '',
      imageUrl: banner.imageUrl || '',
      textColor: banner.textColor || '#FFFFFF',
      bgColor: banner.bgColor || '#EA580C',
      fontSize: banner.fontSize || 13,
      bold: banner.bold ?? true,
    });
    setSelectedImage(null);
    setImagePreview(banner.type === 'image' ? banner.imageUrl : null);
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditingBanner(null); };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) return alert('Only JPG, PNG, WebP allowed');
    if (file.size > 5 * 1024 * 1024) return alert('Max 5MB');
    setSelectedImage(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const uploadImage = async () => {
    if (!selectedImage) return form.imageUrl || null;
    const timestamp = Date.now();
    const storageRef = ref(storage, `banners/${timestamp}_${selectedImage.name}`);
    await uploadBytes(storageRef, selectedImage);
    return await getDownloadURL(storageRef);
  };

  const handleSave = async () => {
    if (form.type === 'text' && !form.text.trim()) return alert('Text is required');
    if (form.type === 'image' && !selectedImage && !form.imageUrl) return alert('Image is required');

    try {
      setSaving(true);
      let imageUrl = form.imageUrl || null;
      if (form.type === 'image') imageUrl = await uploadImage();

      const payload = {
        type: form.type,
        target: activeTab,
        visible: editingBanner?.visible ?? true,
        text: form.type === 'text' ? form.text.trim() : '',
        url: form.url.trim(),
        imageUrl: form.type === 'image' ? imageUrl : null,
        ...(form.type === 'text' && {
          textColor: form.textColor,
          bgColor: form.bgColor,
          fontSize: form.fontSize,
          bold: form.bold,
        }),
        updatedAt: serverTimestamp(),
      };

      if (editingBanner) {
        await updateDoc(doc(db, 'banners', editingBanner.id), payload);
        logAdminActivity({
          actor: currentAdmin, action: 'update_banner',
          targetType: 'banner', targetId: editingBanner.id,
          targetLabel: `${activeTab} · ${form.type}`,
        });
      } else {
        const newRef = await addDoc(collection(db, 'banners'), {
          ...payload,
          order: banners.length,
          createdAt: serverTimestamp(),
        });
        logAdminActivity({
          actor: currentAdmin, action: 'create_banner',
          targetType: 'banner', targetId: newRef.id,
          targetLabel: `${activeTab} · ${form.type}`,
        });
      }
      closeModal();
      fetchBanners();
    } catch (err) {
      console.error('Error saving banner:', err);
      alert('Failed to save banner: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleVisibility = async (banner) => {
    try {
      await updateDoc(doc(db, 'banners', banner.id), { visible: !banner.visible });
      setBanners(prev => prev.map(b => b.id === banner.id ? { ...b, visible: !b.visible } : b));
    } catch (err) {
      console.error('Error toggling visibility:', err);
    }
  };

  const moveOrder = async (index, direction) => {
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= banners.length) return;

    const updated = [...banners];
    // Swap order values
    const tempOrder = updated[index].order;
    updated[index] = { ...updated[index], order: updated[swapIndex].order };
    updated[swapIndex] = { ...updated[swapIndex], order: tempOrder };
    // Sort by new order
    updated.sort((a, b) => a.order - b.order);
    setBanners(updated);

    try {
      await Promise.all([
        updateDoc(doc(db, 'banners', banners[index].id), { order: updated.find(b => b.id === banners[index].id).order }),
        updateDoc(doc(db, 'banners', banners[swapIndex].id), { order: updated.find(b => b.id === banners[swapIndex].id).order }),
      ]);
    } catch (err) {
      console.error('Error reordering:', err);
      fetchBanners(); // Revert on error
    }
  };

  const handleDelete = async (banner) => {
    try {
      setDeletingId(banner.id);
      if (banner.type === 'image' && banner.imageUrl) {
        try {
          const imageRef = ref(storage, banner.imageUrl);
          await deleteObject(imageRef);
        } catch (_) {}
      }
      await deleteDoc(doc(db, 'banners', banner.id));
      logAdminActivity({
        actor: currentAdmin, action: 'delete_banner',
        targetType: 'banner', targetId: banner.id,
        targetLabel: `${banner.audience || ''} · ${banner.type || ''}`.trim(),
      });
      setBanners(prev => prev.filter(b => b.id !== banner.id));
      setConfirmDeleteId(null);
    } catch (err) {
      console.error('Error deleting banner:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const typeIcon = (type) => {
    if (type === 'image') return <PhotoIcon className="w-4 h-4" />;
    return <DocumentTextIcon className="w-4 h-4" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Banners</h1>
          <p className="text-gray-600 mt-1">Manage app banners for drivers and customers</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          Add Banner
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        {TARGET_TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Global closeable toggle */}
      <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 px-5 py-3 shadow-sm">
        <div>
          <p className="text-sm font-semibold text-gray-800">Allow users to close banner</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Applies to all {activeTab === 'driver' ? 'Driver' : 'Customer'} app banners
          </p>
        </div>
        <button
          onClick={() => toggleCloseable(activeTab)}
          disabled={savingCloseable}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
            closeable[activeTab] ? 'bg-blue-600' : 'bg-gray-300'
          }`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
            closeable[activeTab] ? 'translate-x-6' : 'translate-x-1'
          }`} />
        </button>
      </div>

      {/* Banner List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-6 h-6 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-gray-500 mt-2">Loading banners...</p>
          </div>
        ) : banners.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <PhotoIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p>No banners yet for {activeTab === 'driver' ? 'Driver' : 'Customer'} app</p>
            <button onClick={openCreate} className="mt-3 text-blue-600 text-sm font-medium hover:underline">
              Add the first banner
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {banners.map((banner, index) => (
              <div key={banner.id} className="flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors">
                {/* Preview */}
                <div className="w-32 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 flex items-center justify-center border border-gray-200">
                  {banner.type === 'image' && banner.imageUrl ? (
                    <img src={banner.imageUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="px-2 text-center">
                      <p className="text-xs text-gray-600 line-clamp-2">{banner.text}</p>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                      banner.type === 'image' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {typeIcon(banner.type)} {banner.type}
                    </span>
                    {banner.url && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                        <LinkIcon className="w-3 h-3" /> link
                      </span>
                    )}
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      banner.visible ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {banner.visible ? 'Visible' : 'Hidden'}
                    </span>
                  </div>
                  {banner.type === 'text' && (
                    <p className="text-sm text-gray-800 font-medium truncate">{banner.text}</p>
                  )}
                  {banner.url && (
                    <p className="text-xs text-blue-500 truncate">{banner.url}</p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0">
                  {/* Order buttons */}
                  <div className="flex flex-col mr-1">
                    <button
                      onClick={() => moveOrder(index, -1)}
                      disabled={index === 0}
                      className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                      title="Move up"
                    >
                      <ChevronUpIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => moveOrder(index, 1)}
                      disabled={index === banners.length - 1}
                      className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                      title="Move down"
                    >
                      <ChevronDownIcon className="w-4 h-4" />
                    </button>
                  </div>

                  <button
                    onClick={() => toggleVisibility(banner)}
                    className={`p-2 rounded-lg transition-colors ${banner.visible ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`}
                    title={banner.visible ? 'Hide' : 'Show'}
                  >
                    {banner.visible ? <EyeIcon className="w-4 h-4" /> : <EyeSlashIcon className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => openEdit(banner)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  {confirmDeleteId === banner.id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleDelete(banner)}
                        disabled={deletingId === banner.id}
                        className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 font-medium disabled:opacity-50"
                      >
                        {deletingId === banner.id ? '...' : 'Confirm'}
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 font-medium"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmDeleteId(banner.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">
                {editingBanner ? 'Edit Banner' : 'Add Banner'}
              </h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Type selector */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Banner Type</label>
              <div className="grid grid-cols-3 gap-2">
                {TYPE_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setForm(f => ({ ...f, type: opt.value }))}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all text-sm font-medium ${
                      form.type === opt.value
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <opt.icon className="w-5 h-5" />
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Text field (text type only) */}
            {form.type === 'text' && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Text</label>
                <textarea
                  value={form.text}
                  onChange={e => setForm(f => ({ ...f, text: e.target.value }))}
                  rows={3}
                  maxLength={300}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                  placeholder="Banner text..."
                />
                <p className="text-xs text-gray-400 mt-1">{form.text.length}/300</p>
              </div>
            )}

            {/* URL field (optional for both types) */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Link URL <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="url"
                value={form.url}
                onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="https://... (leave empty if not clickable)"
              />
            </div>

            {/* Image upload (image type) */}
            {form.type === 'image' && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Image</label>
                {imagePreview ? (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-32 object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      onClick={() => { setSelectedImage(null); setImagePreview(null); setForm(f => ({ ...f, imageUrl: '' })); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <input ref={fileInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" onChange={handleImageSelect} className="hidden" id="banner-image" />
                    <label htmlFor="banner-image" className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                      <ArrowUpTrayIcon className="w-8 h-8 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-500">Click to upload (JPG, PNG, WebP · max 5MB)</p>
                    </label>
                  </>
                )}
                <p className="text-xs text-gray-400 mt-1">Recommended max height: 120px to avoid large banners in app</p>
              </div>
            )}

            {/* Style options (text only) */}
            {form.type === 'text' && (
              <div className="space-y-3 border border-gray-100 rounded-lg p-4 bg-gray-50">
                <p className="text-sm font-semibold text-gray-700">Style</p>

                {/* Colors */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Background Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={form.bgColor}
                        onChange={e => setForm(f => ({ ...f, bgColor: e.target.value }))}
                        className="w-9 h-9 rounded cursor-pointer border border-gray-300"
                      />
                      <span className="text-xs text-gray-500 font-mono">{form.bgColor}</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Text Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={form.textColor}
                        onChange={e => setForm(f => ({ ...f, textColor: e.target.value }))}
                        className="w-9 h-9 rounded cursor-pointer border border-gray-300"
                      />
                      <span className="text-xs text-gray-500 font-mono">{form.textColor}</span>
                    </div>
                  </div>
                </div>

                {/* Font size */}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Font Size</label>
                  <div className="flex gap-2">
                    {FONT_SIZES.map(s => (
                      <button
                        key={s.value}
                        onClick={() => setForm(f => ({ ...f, fontSize: s.value }))}
                        className={`flex-1 py-1.5 rounded text-xs font-medium border transition-all ${
                          form.fontSize === s.value
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Bold */}
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.bold}
                      onChange={e => setForm(f => ({ ...f, bold: e.target.checked }))}
                      className="w-4 h-4 accent-blue-600"
                    />
                    <span className="text-sm text-gray-700 font-medium">Bold text</span>
                  </label>
                </div>

                {/* Live preview */}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Preview</label>
                  <div
                    className="rounded-xl px-4 py-3 flex items-center justify-between"
                    style={{ backgroundColor: form.bgColor }}
                  >
                    <span
                      style={{
                        color: form.textColor,
                        fontSize: form.fontSize,
                        fontWeight: form.bold ? 700 : 400,
                      }}
                    >
                      {form.text || 'Banner preview text'}
                    </span>
                    {closeable[activeTab] && (
                      <span style={{ color: form.textColor, opacity: 0.7, marginLeft: 8, fontSize: 14 }}>✕</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="flex gap-3 pt-1">
              <button onClick={closeModal} className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors">
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : editingBanner ? 'Save Changes' : 'Add Banner'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
